/**
 * Pure model + geometry for the mobile day / week grids. No React.
 *
 * Event Dates carry the salon wall-clock as browser-local time (see
 * getSalonNow), so days and minutes come straight from the native local
 * getters (never a moment default-timezone conversion).
 */
import moment from "moment";
import { CalendarEventType } from "../../../../../types/calendar";
import { CalendarEvent } from "../../../../../types/transactions";
import {
  DAY_BLOCK_MIN_H,
  FALLBACK_HUE,
  WEEK_BLOCK_MIN_H,
  WEEK_OVERLAP_CAP,
} from "../tokens";

const DAY_MIN = 24 * 60;

/** One appointment or time block, reduced to what the grids render. */
export interface MobileAppt {
  id: string;
  event: CalendarEventType;
  staffId: string;
  dayKey: string;
  startMin: number;
  endMin: number;
  service: string;
  client: string;
  hue: string;
  status: string | null;
  serviceType: string | null;
  isBlock: boolean;
  baseMin: number;
  procMin: number;
  /** Balance owed once the appointment has ended (0 = none). */
  due: number;
  dim: boolean;
  settled: boolean;
  overdue: boolean;
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Local calendar day key, "YYYY-MM-DD". */
export const dayKeyOf = (d: Date): string =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** Local midnight of a "YYYY-MM-DD" key. */
export const dateFromKey = (key: string): Date => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
};

/** Local midnight `n` days after `d`'s day. */
export const addDays = (d: Date, n: number): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/** Sunday (week start) of `d`'s week, local midnight. */
export const startOfWeek = (d: Date): Date => addDays(d, -d.getDay());

/** en-US date label, e.g. { month: "short", day: "numeric" } → "Aug 24". */
export const formatDate = (
  d: Date,
  options: Intl.DateTimeFormatOptions,
): string => d.toLocaleDateString("en-US", options);

const WEEKDAY_NAMES = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const minutesOf = (d: Date): number => d.getHours() * 60 + d.getMinutes();

/** Statuses that never owe a balance on the grid. */
const NO_BALANCE_STATUSES = new Set(["Cancelled", "Declined", "No Show"]);

/**
 * Balance owed on an appointment: its price minus the transaction's net
 * payments, only once the appointment has ended and the transaction isn't
 * paid. (Payments are per transaction, so a multi-service transaction's
 * partial payment is applied against each of its services.)
 */
export function amountDue(event: CalendarEvent, ended: boolean): number {
  if (!ended) return 0;
  if (event.transactionStatus === "paid") return 0;
  if (NO_BALANCE_STATUSES.has(event.status)) return 0;
  const paid = (event.payments || []).reduce(
    (sum, p) => sum + (p.amount || 0) - (p.refunded_amount || 0),
    0,
  );
  return Math.max(0, Math.round(((event.price || 0) - paid) * 100) / 100);
}

export function toMobileAppt(event: CalendarEventType, now: Date): MobileAppt {
  const start = new Date(event.start);
  const end = new Date(event.end);
  const startMin = minutesOf(start);
  const lengthMin = Math.max(
    1,
    Math.round((end.getTime() - start.getTime()) / 60000),
  );
  const endMin = Math.min(DAY_MIN, startMin + lengthMin);
  const ended = end.getTime() <= now.getTime();

  if (event.isTimeBlock) {
    return {
      id: event.id,
      event,
      staffId: event.resourceId,
      dayKey: dayKeyOf(start),
      startMin,
      endMin,
      service: event.title,
      client: "Time block",
      hue: FALLBACK_HUE,
      status: null,
      serviceType: null,
      isBlock: true,
      baseMin: 0,
      procMin: 0,
      due: 0,
      dim: false,
      settled: ended,
      overdue: false,
    };
  }

  const appt = event as CalendarEvent;
  const [serviceName, titleClient] = (appt.title || "").split(" - ");
  const client =
    appt.firstName || appt.lastName
      ? `${appt.firstName || ""} ${appt.lastName || ""}`.trim()
      : titleClient || "";
  const due = amountDue(appt, ended);
  const status = appt.status || null;
  return {
    id: appt.id,
    event,
    staffId: appt.resourceId,
    dayKey: dayKeyOf(start),
    startMin,
    endMin,
    service: serviceName || "",
    client,
    hue: appt.backgroundColor || "#36afc9",
    status,
    serviceType: appt.serviceType || null,
    isBlock: false,
    baseMin: appt.baseDuration || 0,
    procMin: appt.processingTime || 0,
    due,
    dim: status === "Cancelled" || status === "Declined",
    settled: status === "Completed" || (ended && due === 0),
    overdue: ended && status !== "Completed" && due > 0,
  };
}

/** Day keys of every local calendar day a [start, end) span touches. */
function spannedDayKeys(start: Date, end: Date): string[] {
  const keys: string[] = [];
  for (let day = addDays(start, 0); day < end; day = addDays(day, 1)) {
    keys.push(dayKeyOf(day));
  }
  return keys;
}

/** The minutes a span occupies on one local day. */
export interface DaySpan {
  dayKey: string;
  startMin: number;
  endMin: number;
}

/**
 * A [start, end) span as one piece per day it covers (desktop's
 * `showMultiDayTimes`): the first day from its start, the last day to its
 * end, full days between, each within 0–1440 minutes. The grids draw time
 * blocks this way and `findStaffConflict` checks them this way.
 */
export function daySpans(start: Date, end: Date): DaySpan[] {
  const keys = spannedDayKeys(start, end);
  const last = keys.length - 1;
  return keys.map((dayKey, i) => ({
    dayKey,
    startMin: i === 0 ? minutesOf(start) : 0,
    endMin:
      i === last && dayKeyOf(end) === dayKey
        ? Math.max(1, minutesOf(end))
        : DAY_MIN,
  }));
}

/**
 * A multi-day time block as one piece per day it covers (`daySpans`). Each
 * piece keeps the block's `event`, so a tap opens the same time block; ids
 * gain "@YYYY-MM-DD" to stay unique.
 */
function splitBlockByDay(block: MobileAppt): MobileAppt[] {
  const spans = daySpans(
    new Date(block.event.start),
    new Date(block.event.end),
  );
  if (spans.length < 2) return [block];
  return spans.map((span) => ({
    ...block,
    ...span,
    id: `${block.id}@${span.dayKey}`,
  }));
}

/**
 * Every event as grid blocks: appointments stay on their start day; time
 * blocks appear on each day they span (so a vacation blocks every day).
 */
export function toMobileAppts(
  events: CalendarEventType[],
  now: Date,
): MobileAppt[] {
  return events.reduce<MobileAppt[]>((out, event) => {
    const appt = toMobileAppt(event, now);
    return out.concat(appt.isBlock ? splitBlockByDay(appt) : [appt]);
  }, []);
}

// ── Time window ──────────────────────────────────────────────────────────

export interface GridWindow {
  startMin: number;
  endMin: number;
}

type HoursSource = Record<string, unknown> | null | undefined;

const parseClock = (raw: unknown): number | null => {
  if (typeof raw !== "string" || !raw) return null;
  const m = moment(raw, ["h:mm A", "HH:mm", "HH:mm:ss"], true);
  return m.isValid() ? m.hours() * 60 + m.minutes() : null;
};

/** Open/close for one day, in minutes past midnight. */
export interface DayHours {
  open: number;
  close: number;
}

const toDayHours = (rawOpen: unknown, rawClose: unknown): DayHours | null => {
  const open = parseClock(rawOpen);
  const close = parseClock(rawClose);
  if (open === null || close === null || close <= open) return null;
  return { open, close };
};

/** Salon open/close for a date (minutes), or null when closed that day. */
export function salonHoursFor(
  businessInfo: HoursSource,
  date: Date,
): DayHours | null {
  if (!businessInfo) return null;
  const day = WEEKDAY_NAMES[date.getDay()];
  return toDayHours(
    businessInfo[`${day}_open_time`],
    businessInfo[`${day}_end_time`],
  );
}

/**
 * A staff member's hours (the "H:MM AM/PM" pair `staffHoursOn` resolves from
 * their override or weekly schedule) in minutes, or null when not working.
 */
export function staffDayHours(
  raw: { start_time: string; end_time: string } | null,
): DayHours | null {
  return raw ? toDayHours(raw.start_time, raw.end_time) : null;
}

/**
 * The hatched (unavailable) spans of a column, as px offsets in `window`:
 * before open and after close, or the whole window when `hours` is null.
 */
export function offHoursSpans(
  hours: DayHours | null,
  window: GridWindow,
  ppm: number,
): { top: number; height: number }[] {
  if (!hours) {
    return [{ top: 0, height: (window.endMin - window.startMin) * ppm }];
  }
  const spans: { top: number; height: number }[] = [];
  if (hours.open > window.startMin) {
    spans.push({ top: 0, height: (hours.open - window.startMin) * ppm });
  }
  if (hours.close < window.endMin) {
    spans.push({
      top: (hours.close - window.startMin) * ppm,
      height: (window.endMin - hours.close) * ppm,
    });
  }
  return spans;
}

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

/** A Sunday, to walk the seven weekdays from. */
const REF_SUNDAY = new Date(2024, 0, 7);

/** True when the salon has hours on at least one weekday. */
export function hasAnyHours(businessInfo: HoursSource): boolean {
  return WEEKDAYS.some(
    (i) => !!salonHoursFor(businessInfo, addDays(REF_SUNDAY, i)),
  );
}

/** A day is closed only when the salon set hours for some days but not it. */
export function isClosedDay(businessInfo: HoursSource, date: Date): boolean {
  return hasAnyHours(businessInfo) && !salonHoursFor(businessInfo, date);
}

/** The mobile grids always span the whole day, 12 AM to 12 AM (scrollable). */
export const FULL_DAY_WINDOW: GridWindow = { startMin: 0, endMin: DAY_MIN };

// ── Labels ───────────────────────────────────────────────────────────────

/** 540 → "9:00 AM". */
export function fmtMin(m: number): string {
  const h = Math.floor(m / 60) % 24;
  const mm = m % 60;
  return `${h % 12 || 12}:${String(mm).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

/** 540 → "9", 570 → "9:30" (12-hour clock, no meridiem). */
const shortClock = (m: number): string => {
  const h = Math.floor(m / 60) % 24;
  const mm = m % 60;
  return `${h % 12 || 12}${mm ? `:${String(mm).padStart(2, "0")}` : ""}`;
};

const meridiem = (m: number): string =>
  Math.floor(m / 60) % 24 >= 12 ? "p" : "a";

/** A block's time, full form: "9:00 AM – 10:00 AM". */
export const blockTimeLabel = (
  a: Pick<MobileAppt, "startMin" | "endMin">,
): string => `${fmtMin(a.startMin)} – ${fmtMin(a.endMin)}`;

/**
 * A block's time, compact form, as [start + dash, end] so a narrow block can
 * wrap between them: ["9–", "10a"], ["9:30–", "10:15a"], ["11:30a–",
 * "12:15p"] (the start's meridiem only when it differs from the end's).
 */
export const blockTimeParts = (
  a: Pick<MobileAppt, "startMin" | "endMin">,
): [string, string] => {
  const from = meridiem(a.startMin);
  const to = meridiem(a.endMin);
  return [
    `${shortClock(a.startMin)}${from === to ? "" : from}–`,
    `${shortClock(a.endMin)}${to}`,
  ];
};

/** Hour rail label: "9 AM", "Noon", "5 PM". */
export function hourLabel(m: number): string {
  const h = (m / 60) % 24;
  if (h === 12) return "Noon";
  return `${h % 12 || 12} ${h >= 12 ? "PM" : "AM"}`;
}

export interface HourMark {
  minute: number;
  label: string;
  /** Rule offset from the lane top (px). */
  top: number;
  /** Label offset: sits on its rule, the closing label above it. */
  labelTop: number;
  /** Window edge rules are a step darker. */
  edge: boolean;
}

export function hourMarks(window: GridWindow, ppm: number): HourMark[] {
  const marks: HourMark[] = [];
  for (let m = window.startMin; m <= window.endMin; m += 60) {
    const top = (m - window.startMin) * ppm;
    marks.push({
      minute: m,
      label: hourLabel(m),
      top,
      // Edge labels sit inside the grid so 12 AM isn't clipped at either end.
      labelTop:
        m === window.startMin
          ? top + 2
          : m === window.endMin
            ? top - 14
            : top - 6,
      edge: m === window.startMin || m === window.endMin,
    });
  }
  return marks;
}

// ── Block layout ─────────────────────────────────────────────────────────

export interface PlacedBlock {
  appt: MobileAppt;
  /** Side-by-side column index and count within its overlap cluster. */
  col: number;
  cols: number;
  top: number;
  height: number;
}

/** Split `appts` (sorted by start) into chained-overlap clusters. */
function clusters(appts: MobileAppt[]): MobileAppt[][] {
  const sorted = [...appts].sort(
    (a, b) => a.startMin - b.startMin || a.endMin - b.endMin,
  );
  const out: MobileAppt[][] = [];
  let current: MobileAppt[] = [];
  let currentEnd = -1;
  sorted.forEach((a) => {
    if (current.length && a.startMin >= currentEnd) {
      out.push(current);
      current = [];
      currentEnd = -1;
    }
    current.push(a);
    currentEnd = Math.max(currentEnd, a.endMin);
  });
  if (current.length) out.push(current);
  return out;
}

/** Greedy column packing: first column whose last block has ended. */
function packColumns(cluster: MobileAppt[]): number[] {
  const ends: number[] = [];
  return cluster.map((a) => {
    let col = ends.findIndex((e) => e <= a.startMin);
    if (col < 0) {
      col = ends.length;
      ends.push(0);
    }
    ends[col] = a.endMin;
    return col;
  });
}

const blockTop = (a: MobileAppt, window: GridWindow, ppm: number) =>
  (a.startMin - window.startMin) * ppm;

/** Day column: every overlap gets its own side-by-side column. */
export function layoutDayColumn(
  appts: MobileAppt[],
  window: GridWindow,
  ppm: number,
): PlacedBlock[] {
  const placed: PlacedBlock[] = [];
  clusters(appts).forEach((cluster) => {
    const cols = packColumns(cluster);
    const n = Math.max(...cols) + 1;
    cluster.forEach((a, k) => {
      placed.push({
        appt: a,
        col: cols[k],
        cols: n,
        top: blockTop(a, window, ppm),
        height: Math.max((a.endMin - a.startMin) * ppm, DAY_BLOCK_MIN_H),
      });
    });
  });
  return placed;
}

export interface MoreChip {
  startMin: number;
  top: number;
  count: number;
  hidden: MobileAppt[];
}

/**
 * Week column: at most WEEK_OVERLAP_CAP blocks side by side; the rest of a
 * cluster collapses into one `+N` chip at the cluster's start.
 */
export function layoutWeekColumn(
  appts: MobileAppt[],
  window: GridWindow,
  ppm: number,
): { blocks: PlacedBlock[]; more: MoreChip[] } {
  const blocks: PlacedBlock[] = [];
  const more: MoreChip[] = [];
  clusters(appts).forEach((cluster) => {
    const cols = packColumns(cluster);
    const n = Math.min(WEEK_OVERLAP_CAP, Math.max(...cols) + 1);
    const hidden: MobileAppt[] = [];
    cluster.forEach((a, k) => {
      if (cols[k] >= WEEK_OVERLAP_CAP) {
        hidden.push(a);
        return;
      }
      blocks.push({
        appt: a,
        col: cols[k],
        cols: n,
        top: blockTop(a, window, ppm),
        height: Math.max((a.endMin - a.startMin) * ppm, WEEK_BLOCK_MIN_H),
      });
    });
    if (hidden.length) {
      const startMin = Math.min(...cluster.map((a) => a.startMin));
      more.push({
        startMin,
        top: (startMin - window.startMin) * ppm + 2,
        count: hidden.length,
        hidden,
      });
    }
  });
  return { blocks, more };
}

/**
 * Processing time band inside a block (minutes from the block's start), or
 * null. Processing follows the base duration, trimmed to the block length.
 */
export function processingBand(
  a: Pick<
    MobileAppt,
    "isBlock" | "baseMin" | "procMin" | "startMin" | "endMin"
  >,
): { offsetMin: number; minutes: number } | null {
  if (a.isBlock || !a.procMin) return null;
  const dur = a.endMin - a.startMin;
  const offsetMin = Math.min(a.baseMin, Math.max(0, dur - a.procMin));
  const minutes = Math.min(a.procMin, Math.max(0, dur - offsetMin));
  return minutes > 0 ? { offsetMin, minutes } : null;
}

/** Press position → start minute, snapped to 15 and kept 30 min from close. */
export function snapPressMinute(
  offsetY: number,
  window: GridWindow,
  ppm: number,
): number {
  const raw = window.startMin + offsetY / ppm;
  const snapped = Math.round(raw / 15) * 15;
  return Math.min(
    window.endMin - 30,
    Math.max(window.startMin, Math.min(window.endMin, snapped)),
  );
}

/** Date fields: "ddd MMM D, YYYY" ("Thu Oct 1, 2026"). */
export const dayFieldLabel = (d: Date): string =>
  `${formatDate(d, { weekday: "short" })} ${formatDate(d, { month: "short", day: "numeric" })}, ${d.getFullYear()}`;

/** Day view header (design handoff): "Mon 8/24/26". */
export const dayHeaderLabel = (d: Date): string =>
  `${formatDate(d, { weekday: "short" })} ${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;

/** Week view header: the Sun–Sat range, no year ("Aug 23 – Aug 29"). */
export const weekHeaderLabel = (weekStart: Date): string => {
  const end = addDays(weekStart, 6);
  const md = (d: Date) => formatDate(d, { month: "short", day: "numeric" });
  return `${md(weekStart)} – ${md(end)}`;
};

/** List view header: the month ("October 2026"). */
export const monthHeaderLabel = (d: Date): string =>
  formatDate(d, { month: "long", year: "numeric" });

/** The 7 days (Sunday first) of the week containing `date`. */
export function weekDays(date: Date): Date[] {
  const start = startOfWeek(date);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

/** Single-letter day of week ("S", "M", …). */
export const dowLetter = (d: Date): string =>
  formatDate(d, { weekday: "short" }).charAt(0);

/** "Monday, August 24" (accessible day names). */
export const longDayLabel = (d: Date): string =>
  formatDate(d, { weekday: "long", month: "long", day: "numeric" });

export const initialsOf = (first?: string, last?: string): string =>
  `${(first || "").charAt(0)}${(last || "").charAt(0)}`.toUpperCase() || "?";
